/**
 * Load the artist cockpit interior — the real glTF, fetched when an aeroplane is chosen.
 *
 * ## Why this is lazy, and why it is optional
 *
 * Same two reasons as `aircraftModel.ts`, and the numbers are the same order: the three cockpits
 * are 2.7, 0.2 and 4.1 MB, so downloading all of them to fly one would spend most of the 6 MB
 * initial-payload budget on cabins nobody is sitting in. `GLTFLoader` comes in through a dynamic
 * `import()` for the same reason — the parser stays out of the entry chunk.
 *
 * Optional because a missing cockpit must not ground the aeroplane. `cockpit.ts` builds a complete
 * procedural interior for every aircraft from arithmetic, and that stays the load-failure
 * fallback: everything here either succeeds and is adopted, or resolves `null` and leaves the
 * procedural cockpit exactly as it was. There is no state in between.
 *
 * ## The contract this reads
 *
 * Written by `tools/prep-cockpits.py`, and stated in `cockpits-in/README.md`:
 *
 *   - render space, metres, forward −Z, up +Y, right +X, **origin at the pilot's eye**;
 *   - `COCKPIT_INFO` — an empty carrying `extras.supplies`, one of `cabin`, `panel` or
 *     `cabin+panel`. This is the asset saying what it replaces, rather than the runtime keeping a
 *     table of what each aircraft's cockpit happens to contain;
 *   - `STICK` / `YOKE`, `THROTTLE`, `PEDAL_LEFT`, `PEDAL_RIGHT` — movable controls, where the
 *     source has them.
 *
 * `supplies` matters because the three sources are not equivalent. The Cessna and the Extra each
 * carry a modelled instrument panel; the F-16 is a game-ready exterior with a shaped tub, seat,
 * canopy, consoles, pedals, coaming and HUD combiner, and **no instrument faces under the
 * coaming** — verified by rendering it from the eye point. So it declares `cabin`, and SkySim goes
 * on drawing the fighter's panel into it.
 *
 * The live `InstrumentPanel` is never replaced by any of this. Artist geometry supplies the
 * enclosure, the bezels and the surface detail; every moving reading is still SkySim's canvas.
 *
 * `updateFixed` / `updateFrame`: neither. Loading and measurement only; `Cockpit` drives the rest.
 */

import {
  Group,
  Mesh,
  Object3D,
  Raycaster,
  Texture,
  Vector3,
  type BufferGeometry,
  type Material,
} from 'three';
import { PRODUCT_NAME } from '../brand.js';
import { GAUGE_IDS, type GaugeId } from '../aircraft/instruments.js';

/** What an artist cockpit replaces, read off the asset rather than kept in a table here. */
export type CockpitSupplies = 'cabin' | 'panel' | 'cabin+panel';

/** The movable controls a source may carry, by the names the prep tool assigns. */
export interface CockpitModelControls {
  stick: Object3D | null;
  throttle: Object3D | null;
  pedalLeft: Object3D | null;
  pedalRight: Object3D | null;
}

export interface CockpitModel {
  group: Group;
  supplies: CockpitSupplies;
  controls: CockpitModelControls;
  geometries: readonly BufferGeometry[];
  materials: readonly Material[];
  triangles: number;
  /**
   * Where this model's panel face is, or null if it has none in front of the eye.
   *
   * Measured off the geometry by {@link measurePanelAnchor} rather than declared per aircraft,
   * because it is a property of the model and the model already knows it.
   */
  panel: PanelAnchor | null;
  /**
   * Where this model says each live instrument goes, in its own (eye-relative) space [m].
   *
   * Empty for a source that does not name them, and then {@link panel} places the whole block
   * instead. A source that *does* name them wins: `GAUGE_LAYOUT` is one aeroplane's panel and no
   * artist is obliged to have drawn the same one.
   */
  gaugeAnchors: Map<GaugeId, Vector3>;
}

/** Where a normalised cockpit lives. One file, textures embedded, so a cockpit is one request. */
export function cockpitModelUrl(id: string): string {
  return `${import.meta.env.BASE_URL}cockpits/${id}/cockpit.glb`;
}

/** Where an artist panel's face is, in the model's own space — which is eye-relative. */
export interface PanelAnchor {
  /** Mean of the qualifying hit points: the middle of the face the pilot is looking at [m]. */
  position: Vector3;
  /**
   * The *shallowest* qualifying hit — the top of that face.
   *
   * Kept separately because the mean is not where instruments go. The ray fan spans 30°, so on a
   * cockpit with a lower console the steep rays land on the console and drag the mean down and
   * forward: on the Extra that put the whole six-pack below the panel the pilot can see, behind
   * the switch row. The top of the face is a landmark that does not move when a model happens to
   * have a console, and it is where a six-pack sits on every panel ever certificated.
   */
  top: Vector3;
  /** Distance to the nearest qualifying hit [m], which is what decides z-fighting clearance. */
  distanceM: number;
}

/**
 * Where the pilot's instruments should sit, measured from the model itself.
 *
 * A fan of rays from the eye, forward and progressively down, keeping the nearest hit whose face
 * turns back towards the eye — a panel is by definition the first thing below the sight line that
 * faces the pilot. The alternative was a hand-tuned offset per aircraft, which is three constants
 * that go stale the moment a recipe's keep box changes.
 *
 * Rays are also the measurement that does not lie about geometry. Every earlier "the cockpit is
 * empty" reading during this work came from a *picture*, and pictures are a shading model away
 * from the truth: an untextured white panel under a bright white sky renders the exact colour of
 * the sky. A ray either hits something or it does not.
 *
 * Returns null when nothing plausible is in front — a cabin-only asset, or a badly placed eye.
 */
export function measurePanelAnchor(root: Object3D): PanelAnchor | null {
  const raycaster = new Raycaster();
  const origin = new Vector3(0, 0, 0);
  const direction = new Vector3();
  const toEye = new Vector3();
  const centre = new Vector3();
  const top = new Vector3();
  let nearest = Number.POSITIVE_INFINITY;
  let hits = 0;

  // −4° to −34°: below the glareshield, above the footwell. A real panel spans most of that.
  for (let index = 0; index <= 6; index += 1) {
    const pitch = (-4 - index * 5) * (Math.PI / 180);
    direction.set(0, Math.sin(pitch), -Math.cos(pitch)).normalize();
    raycaster.set(origin, direction);
    for (const hit of raycaster.intersectObject(root, true)) {
      // Ignore anything within arm's reach of the eye — that is the canopy bow, not the panel.
      if (hit.distance < 0.25 || hit.distance > 1.4) continue;
      const normal = hit.normal;
      if (normal === undefined) continue;
      // Faces the pilot: the surface normal has a component back along the ray.
      toEye.copy(direction).negate();
      if (normal.dot(toEye) < 0.35) continue;
      if (hits === 0) top.copy(hit.point);
      centre.add(hit.point);
      nearest = Math.min(nearest, hit.distance);
      hits += 1;
      break;
    }
  }
  if (hits === 0) return null;
  return { position: centre.divideScalar(hits), top, distanceM: nearest };
}

/**
 * Fetch and adopt an aircraft's cockpit interior.
 *
 * Resolves to `null` for every failure — a 404 because the cockpit was never baked, a parse error,
 * a network drop — and logs why. The caller keeps the procedural cockpit in that case, which is
 * the whole reason this cannot throw.
 */
export async function loadCockpitModel(id: string): Promise<CockpitModel | null> {
  const url = cockpitModelUrl(id);
  try {
    const { GLTFLoader } = await import('three/addons/loaders/GLTFLoader.js');
    const gltf = await new GLTFLoader().loadAsync(url);
    return adoptCockpitModel(gltf.scene, id);
  } catch (error) {
    console.warn(
      `${PRODUCT_NAME}: no cockpit model for "${id}" (${url}) — using the procedural cockpit.`,
      error,
    );
    return null;
  }
}

/**
 * Turn a parsed glTF scene into the handles `Cockpit` indexes into.
 *
 * Split out from the fetch so it can be exercised without a network or a GPU: everything here is
 * scene-graph arithmetic, which is exactly the part with contract assumptions in it.
 */
export function adoptCockpitModel(scene: Object3D, id: string): CockpitModel {
  const group = new Group();
  group.name = `${id}-cockpit`;
  group.add(scene);

  const geometries: BufferGeometry[] = [];
  const materials: Material[] = [];
  let triangles = 0;

  scene.traverse((object) => {
    if (!(object instanceof Mesh)) return;
    const geometry = object.geometry as BufferGeometry;
    geometries.push(geometry);
    for (const material of Array.isArray(object.material) ? object.material : [object.material]) {
      if (material !== null && material !== undefined) materials.push(material);
    }
    const index = geometry.getIndex();
    const position = geometry.getAttribute('position');
    if (index !== null) triangles += index.count / 3;
    else if (position !== undefined) triangles += position.count / 3;
  });

  const info = scene.getObjectByName('COCKPIT_INFO');
  const declared = info?.userData['supplies'];
  const supplies: CockpitSupplies =
    declared === 'cabin' || declared === 'panel' || declared === 'cabin+panel'
      ? declared
      : 'cabin+panel';

  return {
    group,
    supplies,
    controls: {
      stick: scene.getObjectByName('STICK') ?? scene.getObjectByName('YOKE') ?? null,
      throttle: scene.getObjectByName('THROTTLE') ?? null,
      pedalLeft: scene.getObjectByName('PEDAL_LEFT') ?? null,
      pedalRight: scene.getObjectByName('PEDAL_RIGHT') ?? null,
    },
    geometries,
    materials,
    triangles: Math.round(triangles),
    panel: measurePanelAnchor(scene),
    gaugeAnchors: readGaugeAnchors(scene),
  };
}

/**
 * Release a cockpit model's GPU resources.
 *
 * Separate from the loader because the loader has no idea when the cockpit is finished with, and
 * separate from `Cockpit.dispose` because a model can also be replaced mid-session when the pilot
 * changes aeroplane. Textures are reached through the materials rather than tracked, since a glTF
 * material owns its maps and nothing else here shares them.
 */
export function disposeCockpitModel(model: CockpitModel): void {
  for (const geometry of model.geometries) geometry.dispose();
  for (const material of model.materials) {
    for (const value of Object.values(material as unknown as Record<string, unknown>)) {
      if (value instanceof Texture) value.dispose();
    }
    material.dispose();
  }
}

/**
 * Collect the `GAUGE_<id>` anchors an artist cockpit carries.
 *
 * Written by `tools/prep-cockpits.py` from the source's own needle positions — a needle sits at
 * the centre of its instrument and is named after it, so the artist measured this before anyone
 * asked. Unknown ids are ignored rather than thrown on: a source naming an instrument SkySim does
 * not draw is a source describing a different aeroplane, not a broken file.
 */
export function readGaugeAnchors(scene: Object3D): Map<GaugeId, Vector3> {
  const known = new Set<string>(GAUGE_IDS);
  const anchors = new Map<GaugeId, Vector3>();
  scene.traverse((object) => {
    if (!object.name.startsWith('GAUGE_')) return;
    const id = object.name.slice('GAUGE_'.length);
    if (!known.has(id)) return;
    object.updateWorldMatrix(true, false);
    anchors.set(id as GaugeId, new Vector3().setFromMatrixPosition(object.matrixWorld));
  });
  return anchors;
}
